import type * as React from "react";
import type { BasicPoint } from "./BasicPoint";

/**
 * Represents the location (x,y) in a x,y coordinate space
 */
export interface IPoint {
    readonly type: "point";
    readonly kind: string;

    readonly x: number;
    readonly y: number;

    /**
     * Creates a BasicPoint that can be used to transfer data to a web worker / serialize to JSON / ...
     */
    toBasicPoint(): BasicPoint;

    toString(): string;
}

export abstract class Point {
    public readonly type: "point";
    abstract readonly kind: string;

    protected constructor(
        public readonly x: number,
        public readonly y: number,
    ) {
        this.type = "point";

        if (x === null || x === undefined) throw new Error("X is null or undefined");
        if (y === null || y === undefined) throw new Error("Y is null or undefined");
        if (Number.isNaN(x)) throw new Error("X is NaN");
        if (Number.isNaN(y)) throw new Error("Y is NaN");
    }

    toBasicPoint() {
        return { x: this.x, y: this.y };
    }

    toString() {
        const { x, y } = this;
        return `(${x.toFixed(2)}, ${y.toFixed(2)})`;
    }
}

export interface IPointInWindow extends IPoint {}

/**
 * A point in the coordinate space of the current HTML window
 */
export class PointInWindow implements IPointInWindow {
    readonly kind = "point-in-window";
    readonly type = "point" as const;

    readonly x: number;
    readonly y: number;

    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    toBasicPoint(): BasicPoint {
        return { x: this.x, y: this.y };
    }

    toString(): string {
        const { x, y } = this;
        return `(${x.toFixed(2)}, ${y.toFixed(2)})`;
    }

    static fromReactMouseEvent(e: React.MouseEvent) {
        return new PointInWindow(e.clientX, e.clientY);
    }

    static fromMouseEvent(e: MouseEvent) {
        return new PointInWindow(e.clientX, e.clientY);
    }

    static fromReactPointerEvent(e: React.PointerEvent) {
        return new PointInWindow(e.clientX, e.clientY);
    }

    static fromPointerEvent(e: PointerEvent) {
        return new PointInWindow(e.clientX, e.clientY);
    }

    static fromReactTouch(touch: React.Touch) {
        return new PointInWindow(touch.clientX, touch.clientY);
    }

    static fromTouch(touch: Touch) {
        return new PointInWindow(touch.clientX, touch.clientY);
    }
}
